/**
 * Conversation list sidebar (§7g — scope-aware extension of the v1 component).
 *
 * Two modes, chosen by the `projectId` prop:
 *   - project mode  (projectId set)  — loads /api/conversations?projectId=...,
 *                                      conversation links target
 *                                      /projects/{id}/chat/{personaId}/{convId},
 *                                      "+ New" links to the project's
 *                                      conversations tab where the persona
 *                                      picker modal lives
 *   - standalone    (projectId null) — loads all user conversations (v1
 *                                      behaviour), links target
 *                                      /coaches/chat/{personaId}/{convId},
 *                                      "+ New" links to /coaches
 *
 * In standalone mode the component still shows project-scoped
 * conversations that belong to the user — those rows just link into
 * the project view instead of the coaches flow, so the link target
 * branches on whether each conversation has a projectId.
 */

import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStore } from "../store/useStore.js";
import { useChatNavigation } from "../hooks/useChatNavigation.js";

export default function Sidebar({ open = false, onClose, projectId = null }) {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const conversations = useStore((s) => s.conversations);
  const loading = useStore((s) => s.conversationsLoading);
  const loadConversations = useStore((s) => s.loadConversations);
  const removeConversation = useStore((s) => s.removeConversation);
  const { chatPath, roundtablePath, scopeRoot } = useChatNavigation(projectId);

  useEffect(() => {
    loadConversations(projectId);
  }, [loadConversations, projectId]);

  const handleDelete = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm("Delete this conversation?")) return;
    await removeConversation(id);
    // If we just deleted the currently-open conversation, pop back to
    // the conversation-parent route (project view or coaches).
    if (id === conversationId) {
      navigate(scopeRoot);
    }
  };

  const handleNavigate = () => {
    onClose?.();
  };

  // "+ New" target depends on scope: in a project, back to the project
  // view (which in Commit E opens a persona picker); standalone, back
  // to the coaches grid. `scopeRoot` encapsulates that branching.
  const newConvHref = scopeRoot;

  // Scope filtering: in project mode the backend already scopes via
  // `?projectId=...`. In standalone mode the backend returns ALL user
  // conversations including project-scoped ones — filter those out so
  // the standalone sidebar only shows conversations that actually
  // belong to the coaches context. Project-scoped conversations live
  // in their project view; surfacing them in standalone would yank the
  // user out of the coaches flow on click, which is surprising UX.
  const visibleConversations = projectId
    ? conversations
    : conversations.filter((c) => !c.projectId);

  // Per-row link target: single-type rows link to the chat path,
  // roundtable-type rows link to the roundtable path. In project mode
  // the href is scoped to the current project; in standalone mode
  // every row links under /coaches (safe because project-scoped rows
  // are already filtered out above). URL construction runs through
  // `useChatNavigation` (G9 / JAM-144) so the scope-aware branching
  // stays in one place.
  //
  // Defensive fallback: a malformed single-type row without a
  // personaId (e.g. data-migration drift) would otherwise produce a
  // `/coaches/chat/undefined/:id` href. Return the base URL so the
  // row is still clickable without producing a 404-ish navigation.
  const conversationHref = (c) => {
    if (c.type === "roundtable") {
      return roundtablePath(c.id);
    }
    if (!c.personaId) return scopeRoot;
    return chatPath(c.personaId, c.id);
  };

  return (
    <>
      {open && (
        <div
          className="sidebar-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside className={`sidebar ${open ? "mobile-open" : ""}`}>
        <div className="sidebar-header">
          <h3>Conversations</h3>
          <div className="sidebar-header-actions">
            <Link
              to={newConvHref}
              className="btn btn-ghost btn-sm"
              onClick={handleNavigate}
            >
              + New
            </Link>
            <button
              type="button"
              className="sidebar-close"
              onClick={onClose}
              aria-label="Close sidebar"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="sidebar-list">
          {loading && <div className="sidebar-empty">Loading…</div>}
          {!loading && visibleConversations.length === 0 && (
            <div className="sidebar-empty">No conversations yet</div>
          )}
          {visibleConversations.map((c) => {
            const isRoundtable = c.type === "roundtable";
            const subtitle = isRoundtable
              ? (c.personaIds || []).join(" · ")
              : c.personaId;
            return (
              <Link
                key={c.id}
                to={conversationHref(c)}
                onClick={handleNavigate}
                className={`sidebar-item ${c.id === conversationId ? "active" : ""}`}
              >
                <div className="sidebar-item-title">
                  {isRoundtable && (
                    <span
                      className="conversation-badge"
                      aria-label="Roundtable"
                    >
                      RT
                    </span>
                  )}
                  {c.title || "Untitled"}
                </div>
                <div className="sidebar-item-meta">
                  <span>{subtitle}</span>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, c.id)}
                    className="sidebar-item-delete"
                    aria-label="Delete conversation"
                  >
                    ✕
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      </aside>
    </>
  );
}
